// src/pages/personal/analytics/MarketingExpensesSection/components/ExpensesChart.tsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import { TEXT_STYLES } from '@/theme';
import { Flex } from '@/components/ui/Layout';
import { 
  BarChart as RechartsBarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer 
} from 'recharts';
import { ExpensesDataPoint } from '../hooks/useMarketingExpensesData';
import { MarketingMetricType } from '../MarketingExpensesSection';
import { Period } from '../../types/types';
import { 
  formatNumber, 
  formatPercent, 
  formatPrice, 
  formatCompactNumber 
} from '@/utils/Formatters';
import { LOCALES, NUMBER_FORMATS } from '@/utils/constants';
import { useBusinessCurrency } from '@/hooks/useBusinessCurrency';

interface ExpensesChartProps {
  data: ExpensesDataPoint[];
  metric: MarketingMetricType;
  period: Period;
  isLoading: boolean;
  dateRange?: { startDate: Date; endDate: Date };
}

// Карта цветов для различных метрик
const metricColors: Record<MarketingMetricType, string> = {
  totalSpend: '#7B1FA2',
  impressions: '#1976D2',
  clicks: '#2E7D32',
  uniqueReach: '#FF9800',
  ctr: '#F44336'
};

const ExpensesChart: React.FC<ExpensesChartProps> = ({ 
  data, 
  metric,
  isLoading}) => {
  const { t, i18n } = useTranslation(['expenses', 'common']);
  const { currencySymbol } = useBusinessCurrency();
  const currentLocale = i18n.language || LOCALES.DEFAULT;
  
  // Проверка наличия данных
  const noDataAvailable = data.length === 0 || 
                          data.every(item => !item[metric] || item[metric] === 0);
  
  // Функция форматирования значения для tooltip в зависимости от типа метрики
  const formatTooltipValue = (value: number): string => {
    switch (metric) {
      case 'ctr':
        return formatPercent(value / 100, NUMBER_FORMATS.PERCENT_PRECISION, true, currentLocale);
      case 'totalSpend':
        return formatPrice(value, currencySymbol, currentLocale);
      case 'impressions':
      case 'clicks':
      case 'uniqueReach':
      default:
        return formatNumber(value, undefined, true, currentLocale);
    }
  };

  // Функция форматирования значения для оси Y
  const formatYAxisTick = (value: number) => {
    // Для больших чисел используем компактное форматирование
    if (value >= 1000) {
      return formatCompactNumber(value, NUMBER_FORMATS.DEFAULT_PRECISION);
    }
    
    // Для процентных метрик
    if (metric === 'ctr') {
      return `${value}%`;
    }
    
    // Для остальных используем обычное форматирование чисел
    return formatNumber(value, undefined, true, currentLocale);
  };

  return (
    <div className="relative overflow-hidden rounded-lg shadow-sm border border-[#e5e7eb] bg-white">
      <div className="bg-[#F5FAF5] px-5 py-3 border-b border-[#e5e7eb]">
        <Flex justify="between" align="center">
          <div>
            <h2 className={TEXT_STYLES.h2}>{t(`marketing.charts.${metric}.title`)}</h2>
            <p className="text-gray-500">{t('marketing.charts.subtitle')}</p>
          </div>
        </Flex>
      </div>
      <div className="p-3">
        <div className="h-[400px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <RechartsBarChart
              data={data}
              barSize={20}
              margin={{ top: 20, right: 30, left: 30, bottom: 20 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="name" 
                tick={{ fill: '#465357' }}
                height={50}
              />
              <YAxis 
                tick={{ fill: '#465357' }} 
                tickFormatter={formatYAxisTick}
                label={{ 
                  value: (metric === 'ctr') ? '%' : '', 
                  angle: -90, 
                  position: 'insideLeft' 
                }}
                width={60}
              />
              <Tooltip 
                content={(tooltipProps) => {
                  const { active, payload, label } = tooltipProps;
                  
                  if (!active || !payload || !payload.length) {
                    return null;
                  }
                  
                  const value = payload[0].value;
                  
                  // Форматируем значение в зависимости от типа метрики
                  const formattedValue = value !== undefined 
                    ? formatTooltipValue(Number(value))
                    : '0';
                  
                  return (
                    <div className="bg-white p-2 border border-[#A7ABAA] rounded-lg shadow-md text-sm">
                      <div className="font-medium mb-1 pb-1 border-b border-[#A7ABAA]/20">
                        {label}
                      </div>
                      <div className="space-y-1">
                        <div className="flex items-center">
                          <div 
                            className="w-3 h-3 mr-2 rounded-sm" 
                            style={{ backgroundColor: payload[0].color }}
                          ></div>
                          <span className="mr-2">{t(`marketing.metrics.${metric}`)}:</span>
                          <span className="font-medium">{formattedValue}</span>
                        </div>
                      </div>
                    </div>
                  );
                }}
              />
              <Bar
                dataKey={metric}
                name={t(`marketing.metrics.${metric}`)}
                fill={metricColors[metric]}
                radius={[4, 4, 0, 0]}
              />
            </RechartsBarChart>
          </ResponsiveContainer>
        </div>
      </div>
      {isLoading && (
        <div className="absolute inset-0 bg-white/80 flex items-center justify-center flex-col">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#245D33]"></div>
          <p className="mt-2 text-gray-600">{t('common:loading')}</p>
        </div>
      )}
      {noDataAvailable && !isLoading && (
        <div className="absolute inset-0 bg-white/80 flex items-center justify-center flex-col">
          <p className="text-gray-600">{t('marketing.no_data_for_period', 'Нет данных за выбранный период')}</p>
        </div>
      )}
    </div>
  );
};

export default ExpensesChart;